import { observer } from "mobx-react-lite";
import { v4 as uuid } from "uuid";
import { useRootStore } from "../../../../stores/hooks";
import { DefaultSection } from "../../Sections/DefaultSection";
import { TriggerSection } from "../../Sections/TriggerSection";
import { ActionSection } from "../../Sections/ActionSection";
import * as A from "../../../../stores/automation/mergers/actions";
import type { AuthAttempt } from "../../../../auth";
import type { CurrSection } from "../../types";
import { useEffect, useState } from "react";
import { LoopingSection } from "../../Sections/LoopingSection";
import { BranchesBlockSection } from "../../Sections/BranchesBlockSection";
import { BranchSection } from "../../Sections/BranchSection";
import { NestedLoopSection } from "../../Sections/NestedLoopSection";

export const SectionSwitch = observer<{
  section: CurrSection;
  setAuthAttempt: (auth: AuthAttempt) => void;
  setSection: (section: CurrSection) => void;
  setIsActionLoading: (isLoading: boolean) => void;
}>(({ section, setAuthAttempt, setSection, setIsActionLoading }) => {
  const [path, setPath] = useState<string>();
  const store = useRootStore();
  const { candidate } = store.automations;
  const actionGroupType =
    candidate.currentActionGroup?.config.oneofKind ?? "actions";

  useEffect(() => {
    setPath(undefined);
  }, [section]);

  const onChangeAction = (integrationId: string, actionId: string): void => {
    if (section?.type !== "action") return;

    const previous = candidate.allActions.find((a) => a.hash === section.hash);
    const previousActionReference = previous?.actionReference;

    const nextHash = uuid();
    candidate.setActions(
      [
        A.ejectActionReferences(section.hash),
        A.mergeReferenceIds(section.hash, integrationId, actionId, nextHash),
      ],
      actionGroupType,
    );

    if (previousActionReference !== undefined) {
      candidate.looping.clearSourceIfActionReference(previousActionReference);
    }

    setSection({ type: "action", hash: nextHash });
  };
  const onClickTest = (path?: string): void => {
    setPath(path);
  };

  switch (section?.type) {
    case "trigger":
      return (
        <TriggerSection setAuthAttempt={setAuthAttempt} candidate={candidate} />
      );
    case "action":
      return (
        <ActionSection
          key={section.hash}
          actionHash={section.hash}
          setAuthAttempt={setAuthAttempt}
          onChangeAction={onChangeAction}
          setIsLoading={setIsActionLoading}
          onClickTest={onClickTest}
          path={path}
        />
      );
    case "looping":
      return <LoopingSection />;
    case "branches":
      return (
        <BranchesBlockSection
          key={`branches-${section.instanceId}`}
          instanceId={section.instanceId}
          setSection={setSection}
        />
      );
    case "branch":
      return (
        <BranchSection
          key={`branch-${section.instanceId}-${section.branchHash}`}
          instanceId={section.instanceId}
          branchHash={section.branchHash}
          setSection={setSection}
        />
      );
    case "nested-loop":
      return (
        <NestedLoopSection
          key={`nested-loop-${section.instanceId}`}
          instanceId={section.instanceId}
          setSection={setSection}
        />
      );
    default:
      return <DefaultSection />;
  }
});
SectionSwitch.displayName = "SectionSwitch";
